import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

export interface Ad {
  id: string;
  title: string;
  description: string;
  image_url?: string;
  link?: string;
  background_color?: string;
  promoId?: string; // Optional promotion ID for tracking
}

interface AdCarouselProps {
  ads: Ad[];
  autoPlayInterval?: number; // in milliseconds, default 5000ms
  onAdClick?: (ad: Ad) => void; // Optional custom click handler
}

export const AdCarousel: React.FC<AdCarouselProps> = ({
  ads,
  autoPlayInterval = 5000,
  onAdClick
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  // Auto-play functionality
  useEffect(() => {
    if (!isAutoPlaying || ads.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) =>
        prevIndex === ads.length - 1 ? 0 : prevIndex + 1
      );
    }, autoPlayInterval);

    return () => clearInterval(interval);
  }, [currentIndex, isAutoPlaying, ads.length, autoPlayInterval]);

  const goToPrevious = useCallback(() => {
    setIsAutoPlaying(false);
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? ads.length - 1 : prevIndex - 1
    );
    // Resume auto-play after 10 seconds
    setTimeout(() => setIsAutoPlaying(true), 10000);
  }, [ads.length]);

  const goToNext = useCallback(() => {
    setIsAutoPlaying(false);
    setCurrentIndex((prevIndex) =>
      prevIndex === ads.length - 1 ? 0 : prevIndex + 1
    );
    // Resume auto-play after 10 seconds
    setTimeout(() => setIsAutoPlaying(true), 10000);
  }, [ads.length]);

  const goToSlide = useCallback((index: number) => {
    setIsAutoPlaying(false);
    setCurrentIndex(index);
    // Resume auto-play after 10 seconds
    setTimeout(() => setIsAutoPlaying(true), 10000);
  }, []);

  const handleAdClick = useCallback((ad: Ad) => {
    // Use custom click handler if provided, otherwise default behavior
    if (onAdClick) {
      onAdClick(ad);
    } else if (ad.link) {
      window.open(ad.link, '_blank');
    }
  }, [onAdClick]);

  if (ads.length === 0) {
    return null;
  }

  const currentAd = ads[currentIndex];

  return (
    <div className="relative w-full overflow-hidden rounded-xl shadow-sm">
      {/* Carousel Container - Fixed height, responsive width */}
      <div
        className="relative h-48 transition-all duration-500 ease-in-out cursor-pointer"
        style={{
          backgroundColor: currentAd.background_color || '#f3f4f6'
        }}
        onClick={() => handleAdClick(currentAd)}
      >
        {/* Ad Image (if available) - object-contain to show full image without cropping */}
        {currentAd.image_url && (
          <img
            src={currentAd.image_url}
            alt={currentAd.title}
            className="absolute inset-0 w-full h-full object-contain"
          />
        )}

        {/* Gradient Overlay for better text readability */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent pointer-events-none" />

        {/* Ad Content */}
        <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
          <h3 className="text-lg font-bold mb-1 line-clamp-2">{currentAd.title}</h3>
          <p className="text-sm opacity-90 line-clamp-2">{currentAd.description}</p>
        </div>

        {/* Navigation Arrows */}
        {ads.length > 1 && (
          <>
            <button
              onClick={(e) => {
                e.stopPropagation();
                goToPrevious();
              }}
              className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/40 backdrop-blur-sm rounded-full p-2 transition-colors"
              aria-label="Previous ad"
            >
              <ChevronLeft className="w-5 h-5 text-white" />
            </button>

            <button
              onClick={(e) => {
                e.stopPropagation();
                goToNext();
              }}
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/40 backdrop-blur-sm rounded-full p-2 transition-colors"
              aria-label="Next ad"
            >
              <ChevronRight className="w-5 h-5 text-white" />
            </button>
          </>
        )}
      </div>

      {/* Pagination Dots */}
      {ads.length > 1 && (
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex space-x-2">
          {ads.map((_, index) => (
            <button
              key={index}
              onClick={(e) => {
                e.stopPropagation();
                goToSlide(index);
              }}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                index === currentIndex
                  ? 'bg-white w-6'
                  : 'bg-white/50 hover:bg-white/75'
              }`}
              aria-label={`Go to ad ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};
